
import React, { useState, useCallback } from 'react';
import { useQuery, gql } from '@apollo/client';
import { debounce } from 'lodash';


const GET_ALL_ITEMS = gql`
  query GetAllItems {
    getAllItems {
    productid
    sku 
    Brand
    itemname
    category 
    sellingunit
    mcpurchaseprice
    mcsellingprice
    packof1
    packof2
    packof5
    packof10
    }
  }
`;

const ItemsList = () => {
  const [searchTerms, setSearchTerms] = useState({
    productid: '',
    sku: '',
    Brand: '',
    itemname: '',
    category: '',
    sellingunit: '',
    mcpurchaseprice: '',
    mcsellingprice: '',
    packof1: '',
    packof2: '',
    packof5: '',
    packof10: ''

  });
  const [editingProductId, setEditingProductId] = useState(null);
  const [editingValues, setEditingValues] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(8);

  const { loading, error, data } = useQuery(GET_ALL_ITEMS);


  const filteredProducts = data ? data.getAllItems.filter(product => {
    return Object.entries(searchTerms).every(([key, value]) => {
      if (!value) return true;
      return product[key]?.toString().toLowerCase().includes(value.toLowerCase());
    });
  }) : [];

  const debouncedSearch = useCallback(
    debounce((searchTerms) => {
      setSearchTerms(searchTerms);
      setCurrentPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    debouncedSearch({ ...searchTerms, [name]: value });
  };

  const handleEdit = (productId, product) => {
    setEditingProductId(productId);
    setEditingValues({ ...product });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditingValues(prev => ({ ...prev, [name]: value }));
  };


  const handleDiscard = () => {
    setEditingProductId(null);
  };



  const headers = [
    { key: 'productid', label: 'id' },
    { key: 'sku', label: 'sku' },
    { key: 'brand', label: 'brand' },
    { key: 'itemname', label: 'name' },
    { key: 'category', label: 'category' },
    { key: 'sellingunit', label: 'sunit' },
    { key: 'mcpurchaseprice', label: 'mcpp' },
    { key: 'mcsellingprice', label: 'mspp' },
    { key: 'packof1', label: 'pack1' },
    { key: 'packof2', label: 'pack2' },
    { key: 'packof5', label: 'pack5' },
    { key: 'packof10', label: 'pack10' }
  ];

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return <div className="flex items-center justify-center h-screen">
      <div className="text-2xl font-semibold text-gray-600">Loading...</div>
    </div>;
  }

  if (error) {
    return <div className="flex items-center justify-center h-screen">
      <div className="text-2xl font-semibold text-red-600">Error: {error.message}</div>
    </div>;
  }

  return (
    <div className="flex flex-col h-full p-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Product List</h1>
      <div className="flex-grow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {headers.map(header => (
                  <th key={header.key} className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {header.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr className="bg-white">
                {headers.map(header => (
                  <td key={header.key} className="px-2 py-2">
                    <input
                      type="text"
                      name={header.key}
                      placeholder={`Search ${header.label}`}
                      value={searchTerms[header.key]}
                      onChange={handleSearchChange}
                      className="w-full p-1 border rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </td>
                ))}
                <td></td>
              </tr>
              {currentProducts.map((product) => (
                <tr key={product.productid} className="hover:bg-gray-50">
                  {headers.map(header => (
                    <td key={header.key} className="px-2 py-2 whitespace-normal text-xs text-gray-800">
                      {editingProductId === product.productid ? (
                        <input
                          type="text"
                          name={header.key}
                          value={editingValues[header.key] || ''}
                          onChange={handleInputChange}
                          className="w-full p-1 border rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        product[header.key]
                      )}
                    </td>
                  ))}

                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
        <div className="flex flex-1 justify-between sm:hidden">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Previous
          </button>
          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === Math.ceil(filteredProducts.length / productsPerPage)}
            className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{indexOfFirstProduct + 1}</span> to <span className="font-medium">{Math.min(indexOfLastProduct, filteredProducts.length)}</span> of{' '}
              <span className="font-medium">{filteredProducts.length}</span> results
            </p>
          </div>
          <div>
            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              {[...Array(Math.ceil(filteredProducts.length / productsPerPage))].map((_, index) => (
                <button
                  key={index}
                  onClick={() => paginate(index + 1)}
                  className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${currentPage === index + 1
                    ? 'z-10 bg-indigo-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                    : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                    }`}
                >
                  {index + 1}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ItemsList;

